import { QuoteBasicDetail } from "../models/Quotes/QuoteBasicDetail";
import { QUOTE_FIELD_SYMBOLS } from "../pages/Products/EditItem/Constants";

const formulas: Record<string, string> = {
    "commissionPercent": 'Lookup',
    "commissionDollars": "dnPrice * commissionPercent",
    "dnToUMAPMultiplier": 'Lookup',
    "dnPrice": "umapPrice / dnToUMAPMultiplier",
    "percentOfFOB": 'Lookup',
    "dutyCost": "fobCost * percentOfFOB",
    "cubeMultiplier": 'Lookup',
    "tariffPercent": 'Lookup',
    "tariffDollars": "fobCost * tariffPercent",
    "royaltyPercent": 'Lookup',
    "royaltyDollars": "royaltyPercent / dnPrice",
    "freightCost": "ipCube * cubeMultiplier",
    "landedCost": "tariffDollars + freightCost + dutyCost + fobCost",
    "grossGMDollars": "dnPrice - landedCost",
    "grossGMPercent": "(grossGMDollars / dnPrice) * 100",
    "netMarginDollars": "grossGMDollars - royaltyDollars - commissionDollars",
    "netMargin": "(netMarginDollars / dnPrice) * 100"
};

const labelsOfFields = [
    {
        label: "FOB Cost",
        fieldName: "fobCost"
    },
    {
        label: "Quote Date",
        fieldName: "quoteDate"
    },
    {
        label: "Quote Number",
        fieldName: "quoteNumber"
    },
    {
        label: "Notes",
        fieldName: "notes"
    },
    {
        label: "Target FOB Cost",
        fieldName: "targetFOBCost"
    },
    {
        label: "IP Cube",
        fieldName: "ipCube"
    },
    {
        label: "Vendor",
        fieldName: "vendor"
    },
    {
        label: "Tariff Percent",
        fieldName: "tariffPercent"
    },
    {
        label: "Blank Field",
        fieldName: "blankField"
    },
    {
        label: "UMAP Price",
        fieldName: "umapPrice"
    },
    {
        label: "Cube Multiplier",
        fieldName: "cubeMultiplier"
    },
    {
        label: "Freight Cost",
        fieldName: "freightCost"
    },
    {
        label: "Landed Cost",
        fieldName: "landedCost"
    },
    {
        label: "Quote Approved",
        fieldName: "quoteApproved"
    },
    {
        label: "DN Price",
        fieldName: "dnPrice"
    },
    {
        label: "Gross GM Percent",
        fieldName: "grossGMPercent"
    },
    {
        label: "Net Margin",
        fieldName: "netMargin"
    },
    {
        label: "Blank Field",
        fieldName: "blankField"
    },
    {
        label: "UMAP Approved",
        fieldName: "umapApproved"
    },
    {
        label: "Commission Dollars",
        fieldName: "commissionDollars"
    },
    {
        label: "Commission Percent",
        fieldName: "commissionPercent"
    },
    {
        label: "DN to UMAP Multiplier",
        fieldName: "dnToUMAPMultiplier"
    },
    {
        label: "Duty Cost",
        fieldName: "dutyCost"
    },
    {
        label: "Gross GM Dollars",
        fieldName: "grossGMDollars"
    },
    {
        label: "Net Margin Dollars",
        fieldName: "netMarginDollars"
    },
    {
        label: "Percent of FOB",
        fieldName: "percentOfFOB"
    },
    {
        label: "Royalty Dollars",
        fieldName: "royaltyDollars"
    },
    {
        label: "Royalty Percent",
        fieldName: "royaltyPercent"
    },
    {
        label: "Tariff Dollars",
        fieldName: "tariffDollars"
    }
];

const fieldsFromAppSettings = [
    "cubeMultiplier",
    "dnToUMAPMultiplier",
];

export const formatFieldValue = (fieldName: string, value: number | string) => {
    const symbol = QUOTE_FIELD_SYMBOLS[fieldName] || "";
    return symbol === "%" ? `<strong>${value || 0}${symbol}</strong>` : `<strong>${symbol}${value || 0}</strong>`;
};

export const getLabelFromFieldName = (fieldName: string) => {
    const label = labelsOfFields?.find(item => item?.fieldName === fieldName)?.label;

    return label ?? "";
};

export type FormulaInfo = {
    formula: string;
    calculation: string;
    value: number | string;
};

const breakDownFormula = (formula: string, quoteDetails: QuoteBasicDetail) => {
    labelsOfFields.forEach((item) => {
        const regex = new RegExp(`\\b${item.fieldName}\\b`, 'g');
        if (regex.test(formula)) {
            const replacement = `${getLabelFromFieldName(item.fieldName)}: ${formatFieldValue(item.fieldName, quoteDetails[item.fieldName])}`;
            formula = formula.replace(regex, replacement);
        }
    });
    return formula;
};

const getformulaWithLabels = (formula: string) => {
    labelsOfFields.forEach((item) => {
        const regex = new RegExp(`\\b${item.fieldName}\\b`, 'g');
        if (regex.test(formula)) {
            const replacement = getLabelFromFieldName(item.fieldName);
            const updatedLabel = fieldsFromAppSettings.includes(item.fieldName) ? `<span class="text-red">${replacement}<sup>1</sup></span>` : replacement;
            formula = formula.replace(regex, updatedLabel);
        }
    });
    return formula;
};

export const getFormulaInfo = (
    fieldName: string,
    quoteDetails: QuoteBasicDetail
): FormulaInfo => {
    if (quoteDetails) {
        const expression = formulas[fieldName];
        const val = quoteDetails[fieldName] || 0;
        return {
            formula: getformulaWithLabels(expression),
            calculation: `${breakDownFormula(expression, quoteDetails)} = ${formatFieldValue(fieldName, val)}`,
            value: formatFieldValue(fieldName, val),
        };
    }
};
